import { ReactNode, useState } from 'react';
import {
  Box,
  Container,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { HEADER_HEIGHT, Z_INDEX } from '@/config/constants';
import { InviteUserModal } from '@/components/ConfirmModals/InviteUserModal';

const NavIconButton = ({
  url,
  label,
  children,
}: {
  url: string;
  label: string;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = location.pathname === url;

  return (
    <Tooltip title={label} arrow>
      <IconButton
        onClick={() => navigate(url)}
        aria-label={label}
        sx={{
          color: isActive
            ? theme.palette.mainColors.primary
            : theme.palette.greyShades.grey1,
          position: 'relative',
          '&::after': {
            content: isActive ? '""' : 'none',
            position: 'absolute',
            left: 0,
            bottom: '0px',
            width: '100%',
            height: '2px',
            backgroundColor: theme.palette.mainColors.primary,
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
};

export const AdminHeader = () => {
  const theme = useTheme();
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: 'var(--header-glass)',
        backdropFilter: 'blur(14px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
        borderBottom:
          '1px solid color-mix(in srgb, var(--outline-variant) 50%, transparent)',
        boxShadow: '0 8px 28px rgba(0, 8, 30, 0.06)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z_INDEX.HEADER,
      }}
    >
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          height: `${HEADER_HEIGHT}px`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '12px', md: '36px' },
          }}
        >
          <Typography
            variant="h3"
            className="font-display"
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--primary-navy)',
            }}
          >
            ADMIN
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <NavIconButton url="/admin/user" label="User Pipeline Management">
              <i className="fa-light fa-users" />
            </NavIconButton>
            <NavIconButton url="/admin/product" label="Estimate Management">
              <i className="fa-light fa-house-building" />
            </NavIconButton>
            <NavIconButton url="/admin/documents" label="Document Management">
              <i className="fa-light fa-file-contract" />
            </NavIconButton>
            <NavIconButton url="/admin/properties" label="Contract Management">
              <i className="fa-light fa-building-columns" />
            </NavIconButton>
            <NavIconButton url="/admin/loan-pipeline" label="Loan pipeline">
              <i className="fa-light fa-file-invoice-dollar" />
            </NavIconButton>
            <NavIconButton
              url="/admin/loan-checklist-matrices"
              label="Loan application checklists"
            >
              <i className="fa-light fa-list-check" />
            </NavIconButton>
            <NavIconButton url="/admin/clauses" label="Clause dictionary FL/VA">
              <i className="fa-light fa-scroll" />
            </NavIconButton>
            <NavIconButton url="/admin/payments" label="Investor payments">
              <i className="fa-light fa-credit-card" />
            </NavIconButton>
            <NavIconButton url="/admin/agent" label="Agent Assignment">
              <i className="fa-light fa-people-arrows" />
            </NavIconButton>
            <NavIconButton url="/admin/feedback" label="Messenger">
              <i className="fa-light fa-comments" />
            </NavIconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip title="Invite User" arrow>
            <IconButton
              onClick={() => setIsInviteOpen(true)}
              aria-label="Invite User"
              sx={{ color: theme.palette.greyShades.grey1 }}
            >
              <i className="fa-light fa-plus" />
            </IconButton>
          </Tooltip>
          <NavIconButton url="/admin/interest-rates" label="Interest Rates">
            <i className="fa-light fa-percentage" />
          </NavIconButton>
          <NavIconButton url="/admin/email" label="Email Management">
            <i className="fa-light fa-envelopes" />
          </NavIconButton>
          <NavIconButton url="/admin" label="Admin Management">
            <i className="fa-light fa-shield-halved" />
          </NavIconButton>
          <NavIconButton url="/main" label="Back to Main">
            <i className="fa-light fa-turn-right" />
          </NavIconButton>
        </Box>
      </Container>
      <InviteUserModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
      />
    </Box>
  );
};
